import { faEnvelope, faMessage, faPhone } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useMemo } from 'react';

import { Channel } from '@/types/channel';
import Select from '@/ui/components/Form/Select';
import { OptionType } from '@/ui/components/Form/Select/types';
import usFormatPhoneNumber from '@/utils/usFormatPhoneNumber';

import { ChannelId } from '../../types';

import { ChannelSelectProps } from './types';

const CHANNEL_ICONS = {
  sms: faMessage,
  email: faEnvelope,
  voice: faPhone
} satisfies Record<ChannelId, typeof faMessage>;

const PHONE_CHANNELS = new Set<ChannelId>([Channel.SMS, Channel.VOICE]);

const formatSender = (id: ChannelId, sender: string) =>
  PHONE_CHANNELS.has(id) ? usFormatPhoneNumber(sender) : sender;

const buildSubLabel = (id: ChannelId, sender: string, name?: string | null) => {
  const formattedSender = formatSender(id, sender);
  return name ? `from ${name} ${formattedSender}` : `from ${formattedSender}`;
};

const ChannelSelect = ({ channelOptions, selectedOption, onChange }: ChannelSelectProps) => {
  const options = useMemo(
    () =>
      channelOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        subLabel: buildSubLabel(opt.id, opt.sender, opt.name),
        icon: CHANNEL_ICONS[opt.id]
      })),
    [channelOptions]
  );

  const selected = useMemo(
    () => ({
      value: selectedOption?.value ?? '',
      label: selectedOption?.label ?? '',
      subLabel: selectedOption
        ? buildSubLabel(selectedOption.id, selectedOption.sender, selectedOption.name)
        : undefined,
      icon: selectedOption ? CHANNEL_ICONS[selectedOption.id] : undefined
    }),
    [selectedOption]
  );

  const handleChange = useCallback(
    (opt: OptionType) => {
      const channel = channelOptions.find((o) => o.value === opt.value);
      if (channel) onChange(channel);
    },
    [channelOptions, onChange]
  );

  return <Select options={options} selectedOption={selected} onChange={handleChange} />;
};

export default ChannelSelect;
